import { useState, useEffect, useMemo } from "react";
import { useSearch, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import {
  useListInternalCatalog,
  useCreateInternalCatalogItem,
  useUpdateInternalCatalogItem,
  useDeleteInternalCatalogItem,
  useBulkDeleteInternalCatalogItems,
  useBulkUpdateCatalogType,
  useBulkUpdateCatalogCategory,
  getListInternalCatalogQueryKey,
} from "@workspace/api-client-react";
import type { InternalCatalogItem, CostType } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus, Pencil, Trash2, Loader2, Layers, Search, ChevronRight, HardHat, Package, Receipt, Tag, Wrench,
  Sparkles, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ComboboxInput } from "@/components/ui/combobox-input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog";
import { ProGate } from "@/components/pro-gate";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { industryPages } from "@/data/industry-pages";
import { COST_PRESETS, catalogNameKey, type CostPresetItem } from "@/data/cost-presets";

const COST_TYPES = ["labour", "materials", "fees"] as const;
type CostTypeValue = typeof COST_TYPES[number];

const TYPE_CONFIG: Record<CostTypeValue, { label: string; icon: React.ElementType; description: string }> = {
  labour: { label: "Labor", icon: HardHat, description: "Workers, trades, subcontractors" },
  materials: { label: "Materials", icon: Package, description: "Supplies, parts, equipment" },
  fees: { label: "Fees", icon: Receipt, description: "Permits, rentals, overhead" },
};

const catalogSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  unitCost: z.coerce.number().min(0, "Cost must be 0 or more"),
  unit: z.string().optional(),
  category: z.string().optional(),
  type: z.enum(["labour", "materials", "fees"]),
});
type CatalogForm = z.infer<typeof catalogSchema>;

const UNITS = ["each", "hour", "sqft", "lf", "day", "lb", "ton", "yd", "gal", "set", "trip"];

interface PresetIndustry {
  id: string;
  name: string;
  icon: typeof industryPages[number]["icon"];
  description: string;
  items: CostPresetItem[];
}

type ImportResult = { created: number; skipped: number; errors: { row: number; message: string }[] };

function IndustryPresetsSection({ onImportSuccess }: { onImportSuccess: () => void }) {
  const { toast } = useToast();
  const { data: catalog } = useListInternalCatalog();
  const [activeIndustry, setActiveIndustry] = useState<PresetIndustry | null>(null);
  const [importing, setImporting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const presetIndustries: PresetIndustry[] = useMemo(
    () =>
      industryPages
        .filter((ind) => COST_PRESETS[ind.slug])
        .map((ind) => ({
          id: ind.slug,
          name: ind.name,
          icon: ind.icon,
          description: ind.heroSubheading,
          items: COST_PRESETS[ind.slug],
        })),
    [],
  );

  const existingNames = useMemo(
    () => new Set((catalog ?? []).map((c) => catalogNameKey(c.name))),
    [catalog],
  );

  const previewCounts = activeIndustry
    ? activeIndustry.items.reduce(
        (acc, item) => {
          if (existingNames.has(catalogNameKey(item.name))) acc.duplicates++;
          else acc.toAdd++;
          return acc;
        },
        { toAdd: 0, duplicates: 0 },
      )
    : { toAdd: 0, duplicates: 0 };

  const handleConfirm = async () => {
    if (!activeIndustry) return;
    setImporting(true);
    try {
      const rows = activeIndustry.items.map((item) => ({
        name: item.name,
        description: item.description,
        unitCost: item.unitCost,
        unit: item.unit,
        category: item.category,
        type: item.type,
      }));
      const res = await fetch("/api/internal-catalog/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows, source: "preset" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Server error ${res.status}`);
      }
      const result: ImportResult = await res.json();
      onImportSuccess();
      emitTourEvent("import.completed");
      toast({
        title: `${activeIndustry.name} presets imported`,
        description: `Added ${result.created}, skipped ${result.skipped} duplicate${result.skipped !== 1 ? "s" : ""}.${result.errors.length > 0 ? ` ${result.errors.length} row${result.errors.length !== 1 ? "s" : ""} had errors.` : ""}`,
      });
      setActiveIndustry(null);
    } catch (err) {
      toast({
        title: "Import failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);

  const typeLabel: Record<CostPresetItem["type"], string> = {
    labour: "Labor",
    materials: "Materials",
    fees: "Fees",
  };

  return (
    <>
      <Card data-tour-target="import-industry">
        <CardContent className="p-6">
          <button
            type="button"
            className="flex items-start gap-3 w-full text-left"
            onClick={() => setExpanded(v => !v)}
          >
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-base">Import from your industry</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Seed your cost catalog with a curated set of standard labor rates, materials, and fees for your trade. Prices are starting points — review and adjust to match your market.
              </p>
            </div>
            <ChevronRight className={`w-5 h-5 text-muted-foreground shrink-0 mt-0.5 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
          </button>

          {expanded && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-4">
            {presetIndustries.map((ind) => {
              const allExist = ind.items.every((i) => existingNames.has(catalogNameKey(i.name)));
              const Icon = ind.icon;
              return (
                <div
                  key={ind.id}
                  className="border rounded-lg p-4 flex flex-col gap-3 bg-card"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-md bg-muted text-foreground">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm leading-tight">{ind.name}</p>
                        {allExist && catalog && (
                          <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0">
                            <Check className="w-3 h-3" />Already imported
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ind.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-xs text-muted-foreground">{ind.items.length} items</span>
                    <Button
                      size="sm"
                      variant={allExist && catalog ? "outline" : "default"}
                      onClick={() => setActiveIndustry(ind)}
                      className="gap-1.5"
                    >
                      Preview and import
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>}
        </CardContent>
      </Card>

      <Dialog open={!!activeIndustry} onOpenChange={(open) => !open && !importing && setActiveIndustry(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeIndustry && <activeIndustry.icon className="w-5 h-5" />}
              {activeIndustry?.name} cost presets
            </DialogTitle>
            <DialogDescription>
              Review the {activeIndustry?.items.length ?? 0} catalog items below.
              {previewCounts.duplicates > 0 && (
                <> {previewCounts.toAdd} new · {previewCounts.duplicates} already in your catalog (will be skipped).</>
              )}
              {previewCounts.duplicates === 0 && previewCounts.toAdd > 0 && (
                <> All {previewCounts.toAdd} are new to your catalog.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-auto rounded-lg border">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Unit cost</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeIndustry?.items.map((item, i) => {
                  const exists = existingNames.has(catalogNameKey(item.name));
                  return (
                    <TableRow key={i} className={exists ? "opacity-60" : ""}>
                      <TableCell className="text-sm font-medium">{item.name}</TableCell>
                      <TableCell className="text-sm capitalize">{typeLabel[item.type]}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.unit}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.unitCost)}</TableCell>
                      <TableCell>
                        {exists ? (
                          <Badge variant="outline" className="text-[10px]">Skip</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">New</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActiveIndustry(null)}
              disabled={importing}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={importing || previewCounts.toAdd === 0} className="gap-2">
              {importing ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Importing...</>
              ) : previewCounts.toAdd === 0 ? (
                "Nothing new to add"
              ) : (
                <>Add {previewCounts.toAdd} item{previewCounts.toAdd !== 1 ? "s" : ""}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CostCatalog() {
  usePageTitle("Cost Catalog");
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<InternalCatalogItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InternalCatalogItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<CostTypeValue>("materials");
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkTypeOpen, setBulkTypeOpen] = useState(false);
  const [bulkTypeTarget, setBulkTypeTarget] = useState<CostTypeValue>("materials");
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkCategoryTarget, setBulkCategoryTarget] = useState("");
  const searchString = useSearch();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (new URLSearchParams(searchString).get("new") === "1") {
      navigate("/cost-catalog", { replace: true });
      if (isPro) setDialogOpen(true);
    }
  }, [searchString, navigate, isPro]);

  const { data: items, isLoading } = useListInternalCatalog({
    query: { queryKey: getListInternalCatalogQueryKey() }
  });

  const createItem = useCreateInternalCatalogItem();
  const updateItem = useUpdateInternalCatalogItem();
  const deleteItem = useDeleteInternalCatalogItem();
  const bulkDeleteItems = useBulkDeleteInternalCatalogItems();
  const bulkUpdateType = useBulkUpdateCatalogType();
  const bulkUpdateCategory = useBulkUpdateCatalogCategory();

  const form = useForm<CatalogForm>({
    resolver: zodResolver(catalogSchema),
    defaultValues: { name: "", description: "", unitCost: 0, unit: "each", category: "", type: "materials" },
  });

  const watchedType = form.watch("type");

  const openCreate = () => {
    form.reset({ name: "", description: "", unitCost: 0, unit: "each", category: "", type: activeTab });
    setEditItem(null);
    setDialogOpen(true);
  };

  const openEdit = (item: InternalCatalogItem) => {
    const itemType = (COST_TYPES.includes(item.type as CostTypeValue) ? item.type : "materials") as CostTypeValue;
    form.reset({
      name: item.name,
      description: item.description ?? "",
      unitCost: Number(item.unitCost),
      unit: item.unit ?? "each",
      category: item.category ?? "",
      type: itemType,
    });
    setEditItem(item);
    setDialogOpen(true);
  };

  const onSubmit = (data: CatalogForm) => {
    const payload = {
      name: data.name,
      description: data.description || null,
      unitCost: data.unitCost,
      unit: data.unit || null,
      category: data.category || null,
      type: data.type,
    };
    if (editItem) {
      updateItem.mutate({ id: editItem.id, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
          setDialogOpen(false);
          toast({ title: "Catalog item updated" });
        }
      });
    } else {
      if (!isPro) {
        toast({ title: "Pro plan required", description: "Upgrade to Pro to add new cost items.", variant: "destructive" });
        return;
      }
      createItem.mutate({ data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
          setDialogOpen(false);
          toast({ title: "Catalog item added" });
        },
        onError: (err: unknown) => {
          const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status;
          const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          if (status === 403) {
            toast({ title: "Pro plan required", description: message ?? "Upgrade to Pro to add new cost items.", variant: "destructive" });
          } else {
            toast({ title: "Failed to add item", description: message ?? "An unexpected error occurred.", variant: "destructive" });
          }
        },
      });
    }
  };

  const onDelete = () => {
    if (!deleteTarget) return;
    deleteItem.mutate({ id: deleteTarget.id }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        setDeleteTarget(null);
        toast({ title: "Item deleted" });
      }
    });
  };

  const confirmBulkDelete = () => {
    const ids = Array.from(selectedIds);
    bulkDeleteItems.mutate({ data: { ids } }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        setSelectedIds(new Set());
        setBulkDeleteOpen(false);
        toast({ title: `${result.deleted} cost item(s) deleted` });
      },
      onError: (err: unknown) => {
        setBulkDeleteOpen(false);
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? "Failed to delete selected items";
        toast({ title: "Could not delete", description: message, variant: "destructive" });
      },
    });
  };

  const confirmBulkType = () => {
    const ids = Array.from(selectedIds);
    bulkUpdateType.mutate({ data: { ids, type: bulkTypeTarget } }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        setSelectedIds(new Set());
        setBulkTypeOpen(false);
        toast({ title: `${result.updated} item(s) updated to ${TYPE_CONFIG[bulkTypeTarget].label}` });
      },
      onError: () => {
        setBulkTypeOpen(false);
        toast({ title: "Failed to update type", variant: "destructive" });
      },
    });
  };

  const confirmBulkCategory = () => {
    const ids = Array.from(selectedIds);
    bulkUpdateCategory.mutate({ data: { ids, category: bulkCategoryTarget } }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        setSelectedIds(new Set());
        setBulkCategoryOpen(false);
        setBulkCategoryTarget("");
        toast({ title: `${result.updated} item(s) moved to "${bulkCategoryTarget || "Uncategorized"}"` });
      },
      onError: () => {
        setBulkCategoryOpen(false);
        toast({ title: "Failed to update category", variant: "destructive" });
      },
    });
  };

  const lowerSearch = searchQuery.toLowerCase().trim();

  const tabItems = items?.filter(item => {
    const itemType = (COST_TYPES.includes(item.type as CostTypeValue) ? item.type : "materials") as CostTypeValue;
    return itemType === activeTab;
  }) ?? [];

  const filteredItems = lowerSearch
    ? tabItems.filter(item =>
        item.name.toLowerCase().includes(lowerSearch) ||
        (item.description ?? "").toLowerCase().includes(lowerSearch) ||
        (item.category ?? "").toLowerCase().includes(lowerSearch)
      )
    : tabItems;

  const grouped = filteredItems.reduce<Record<string, InternalCatalogItem[]>>((acc, item) => {
    const cat = item.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const visibleCategories = Object.keys(grouped).sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });

  const isCategoryOpen = (cat: string) => {
    if (lowerSearch) return true;
    return openCategories.has(cat);
  };

  const toggleCategory = (cat: string) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const allVisibleIds = filteredItems.map(i => i.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id));
  const someSelected = allVisibleIds.some(id => selectedIds.has(id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleIds));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  const categoryOptionsForType = Array.from(
    new Set(
      (items ?? [])
        .filter(i => {
          const t = (COST_TYPES.includes(i.type as CostTypeValue) ? i.type : "materials") as CostTypeValue;
          return t === watchedType;
        })
        .map(i => i.category)
        .filter(Boolean)
    )
  ) as string[];

  const categoryOptionsForActiveTab = Array.from(
    new Set(
      (items ?? [])
        .filter(i => {
          const t = (COST_TYPES.includes(i.type as CostTypeValue) ? i.type : "materials") as CostTypeValue;
          return t === activeTab;
        })
        .map(i => i.category)
        .filter(Boolean)
    )
  ) as string[];

  const typeCountMap: Record<CostTypeValue, number> = {
    labour: 0,
    materials: 0,
    fees: 0,
  };
  if (items) {
    for (const item of items) {
      const t = (COST_TYPES.includes(item.type as CostTypeValue) ? item.type : "materials") as CostTypeValue;
      typeCountMap[t]++;
    }
  }

  const handleIndustryImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="w-8 h-8 text-primary" />
            Cost Catalog
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Your internal cost reference — never shown to customers.
          </p>
        </div>
        <ProGate feature="COST_CATALOGUE_ADD" mode="overlay">
          <Button onClick={openCreate} className="shrink-0">
            <Plus className="w-4 h-4 mr-2" /> New Cost Item
          </Button>
        </ProGate>
      </div>

      <IndustryPresetsSection onImportSuccess={handleIndustryImportSuccess} />

      {/* Type Tabs */}
      <div className="flex gap-1 p-1 bg-border rounded-lg w-full">
        {COST_TYPES.map(t => {
          const config = TYPE_CONFIG[t];
          const Icon = config.icon;
          const count = typeCountMap[t];
          return (
            <button
              key={t}
              type="button"
              onClick={() => { setActiveTab(t); setSelectedIds(new Set()); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-base font-medium transition-colors",
                activeTab === t
                  ? "bg-card shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{config.label}</span>
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full font-mono",
                activeTab === t ? "bg-primary/10 text-primary" : "bg-muted-foreground/20 text-muted-foreground"
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      {!isLoading && (
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${TYPE_CONFIG[activeTab].label.toLowerCase()} items...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 h-8"
            onClick={() => setBulkTypeOpen(true)}
          >
            <Layers className="w-3.5 h-3.5" />
            Change Type
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 h-8"
            onClick={() => { setBulkCategoryTarget(""); setBulkCategoryOpen(true); }}
          >
            <Tag className="w-3.5 h-3.5" />
            Change Category
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="gap-2 h-8"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete ({selectedCount})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="border border-border/50 rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/30">
                <th className="w-10 px-3 py-3" />
                <th className="px-3 py-3 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-3 text-left font-medium text-muted-foreground">Description</th>
                <th className="px-3 py-3 text-left font-medium text-muted-foreground">Category</th>
                <th className="px-3 py-3 text-right font-medium text-muted-foreground">Unit Cost</th>
                <th className="px-3 py-3 text-left font-medium text-muted-foreground">Unit</th>
                <th className="w-20 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {[...Array(4)].map((_, i) => (
                <tr key={i} className="border-b border-border/30">
                  <td className="px-3 py-3"><Skeleton className="h-4 w-4 rounded" /></td>
                  <td className="px-3 py-3"><Skeleton className="h-4 w-32" /></td>
                  <td className="px-3 py-3"><Skeleton className="h-4 w-48" /></td>
                  <td className="px-3 py-3"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-3 py-3"><Skeleton className="h-4 w-16 ml-auto" /></td>
                  <td className="px-3 py-3"><Skeleton className="h-4 w-12" /></td>
                  <td className="px-3 py-3" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-16 text-center border border-dashed rounded-xl">
          {(() => { const Icon = TYPE_CONFIG[activeTab].icon; return <Icon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />; })()}
          <p className="text-muted-foreground font-medium">
            {lowerSearch
              ? `No ${TYPE_CONFIG[activeTab].label.toLowerCase()} items match your search`
              : `No ${TYPE_CONFIG[activeTab].label.toLowerCase()} items yet`}
          </p>
          {!lowerSearch && (
            <p className="text-xs text-muted-foreground mt-1 mb-4">{TYPE_CONFIG[activeTab].description}</p>
          )}
          {!lowerSearch && (
            <ProGate feature="COST_CATALOGUE_ADD" mode="overlay">
              <Button size="sm" onClick={openCreate} className="mt-2">
                <Plus className="w-4 h-4 mr-2" />
                Add {TYPE_CONFIG[activeTab].label} Item
              </Button>
            </ProGate>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleCategories.map(category => {
            const catItems = grouped[category] as InternalCatalogItem[];
            const isOpen = isCategoryOpen(category);
            const catIds = catItems.map(i => i.id);
            const allCatSelected = catIds.every(id => selectedIds.has(id));
            const someCatSelected = catIds.some(id => selectedIds.has(id)) && !allCatSelected;
            return (
              <div key={category} className="border border-border/50 rounded-lg overflow-hidden bg-card">
                {/* Category header */}
                <div className="flex items-center bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="pl-3 pr-1 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={allCatSelected ? true : someCatSelected ? "indeterminate" : false}
                      onCheckedChange={() => {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (allCatSelected || someCatSelected) {
                            catIds.forEach(id => next.delete(id));
                          } else {
                            catIds.forEach(id => next.add(id));
                          }
                          return next;
                        });
                      }}
                      aria-label={`Select all in ${category}`}
                    />
                  </div>
                  <button
                    type="button"
                    className="flex-1 flex items-center gap-2 px-2 py-3 text-left"
                    onClick={() => toggleCategory(category)}
                  >
                    <ChevronRight
                      className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform duration-200 shrink-0",
                        isOpen && "rotate-90"
                      )}
                    />
                    <Layers className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex-1">
                      {category}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0 pr-2">
                      {catItems.length} {catItems.length === 1 ? "item" : "items"}
                    </span>
                  </button>
                </div>

                {/* Item rows */}
                {isOpen && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-t border-b border-border/50 bg-muted/20">
                        <th className="w-10 px-3 py-2" />
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground hidden md:table-cell">Description</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Unit Cost</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground hidden sm:table-cell">Unit</th>
                        <th className="w-20 px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {catItems.map(item => {
                        const isSelected = selectedIds.has(item.id);
                        return (
                          <tr
                            key={item.id}
                            className={cn(
                              "border-t border-border/30 hover:bg-muted/20 transition-colors cursor-pointer group",
                              isSelected && "bg-primary/5"
                            )}
                            onClick={() => navigate(`/cost-catalog/${item.id}`)}
                          >
                            <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleSelect(item.id)}
                                aria-label={`Select ${item.name}`}
                              />
                            </td>
                            <td className="px-3 py-3 font-medium group-hover:text-primary transition-colors max-w-[160px] truncate">
                              {item.name}
                            </td>
                            <td className="px-3 py-3 text-muted-foreground max-w-[240px] truncate hidden md:table-cell">
                              {item.description || <span className="text-muted-foreground/40">—</span>}
                            </td>
                            <td className="px-3 py-3 text-right font-mono font-semibold text-primary whitespace-nowrap">
                              {formatCurrency(Number(item.unitCost))}
                            </td>
                            <td className="px-3 py-3 text-muted-foreground hidden sm:table-cell">
                              {item.unit || <span className="text-muted-foreground/40">—</span>}
                            </td>
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(item)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => setDeleteTarget(item)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Cost Item" : "New Cost Item"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Type selector — appears first */}
              <FormField control={form.control} name="type" render={({ field }) => (
                <FormItem>
                  <FormLabel>Type *</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      {COST_TYPES.map(t => {
                        const config = TYPE_CONFIG[t];
                        const Icon = config.icon;
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => field.onChange(t)}
                            className={cn(
                              "flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-xs font-medium transition-all",
                              field.value === t
                                ? "border-primary bg-primary/5 text-primary"
                                : "border-border text-muted-foreground hover:border-border/80 hover:bg-muted/30"
                            )}
                          >
                            <Icon className="w-4 h-4" />
                            {config.label}
                          </button>
                        );
                      })}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="name" render={({ field }) => (
                <FormItem><FormLabel>Item Name *</FormLabel><FormControl><Input placeholder="e.g. Electrician labor" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="description" render={({ field }) => (
                <FormItem><FormLabel>Description</FormLabel><FormControl><Textarea placeholder="Internal notes about this cost item..." className="min-h-[60px]" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="unitCost" render={({ field }) => (
                  <FormItem><FormLabel>Unit Cost ($) *</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="unit" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors"
                        {...field}
                      >
                        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={form.control} name="category" render={({ field }) => (
                <FormItem>
                  <FormLabel>Category <span className="text-muted-foreground text-xs">(optional sub-group)</span></FormLabel>
                  <FormControl>
                    <ComboboxInput
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      suggestions={categoryOptionsForType}
                      placeholder="e.g. Electrical, Framing, Roofing"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createItem.isPending || updateItem.isPending}>
                  {(createItem.isPending || updateItem.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editItem ? "Save Changes" : "Add Item"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm (single) */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This item will be removed from the catalog. Existing costs on quotes won't be affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirm */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        itemLabel="cost item"
        onConfirm={confirmBulkDelete}
        isPending={bulkDeleteItems.isPending}
      />

      {/* Bulk Change Category Dialog */}
      <Dialog open={bulkCategoryOpen} onOpenChange={(open) => { setBulkCategoryOpen(open); if (!open) setBulkCategoryTarget(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Category for {selectedCount} Item{selectedCount !== 1 ? "s" : ""}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-muted-foreground">Select an existing category or type a new one.</p>
            <ComboboxInput
              value={bulkCategoryTarget}
              onChange={setBulkCategoryTarget}
              suggestions={categoryOptionsForActiveTab}
              placeholder="e.g. Electrical, Framing, Roofing"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCategoryOpen(false)}>Cancel</Button>
            <Button onClick={confirmBulkCategory} disabled={bulkUpdateCategory.isPending}>
              {bulkUpdateCategory.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Change Type Dialog */}
      <AlertDialog open={bulkTypeOpen} onOpenChange={setBulkTypeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Type for {selectedCount} Item{selectedCount !== 1 ? "s" : ""}</AlertDialogTitle>
            <AlertDialogDescription>
              Select the new type to assign to the selected items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 py-2">
            {COST_TYPES.map(t => {
              const config = TYPE_CONFIG[t];
              const Icon = config.icon;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setBulkTypeTarget(t)}
                  className={cn(
                    "flex-1 flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border text-sm font-medium transition-all",
                    bulkTypeTarget === t
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border text-muted-foreground hover:border-border/80 hover:bg-muted/30"
                  )}
                >
                  <Icon className="w-5 h-5" />
                  {config.label}
                </button>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmBulkType(); }}
              disabled={bulkUpdateType.isPending}
            >
              {bulkUpdateType.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Change to {TYPE_CONFIG[bulkTypeTarget].label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
